import { useQuery } from '@tanstack/react-query'
import to from 'await-to-js'
import { AxiosError } from 'axios'
import TableButtonAction from 'components/Buttons/TableButtons/TableButtonAction'
import TableButtonProduct from 'components/Buttons/TableButtons/TableButtonProduct'
import { DataTable, useDataTableContext } from 'components/DataTable'
import {
  filterStoreRepo,
  isShowHiddenActive,
  useFilterParams,
} from 'components/Filters/FilterStore'
import { FilterChips } from 'components/Filters/components/FilterChips/FilterChips'
import DistributorAssignModal from 'components/Modals/AssignmentModal/DistributorAssignModal'
import TagAssignModal from 'components/Modals/AssignmentModal/TagAssignModal'
import { ColumnSelectorRecipient } from 'components/Modals/ColumnModal/ColumnModal'
import {
  CreateContactRequestJobModal,
  MAX_OPPORTUNITIES_REQUESTED,
} from 'components/Modals/CreateContactRequestJobModal'
import ChainExportModal from 'components/Modals/ExportModal/ChainExportModal'
import MenuModal from 'components/Modals/MenuModal/MenuModal'
import OpportunitiesTableFooterControls from 'components/Opportunities/OpportunitiesTableFooterControls'
import { ChainsFilterset } from 'components/Tables/ChainsTable/ChainsFilterset'
import { CHAIN_COLUMNS } from 'components/DataExpansionComponents/Chains/chainColumnsInfo'
import { UNIFIED_CHAINS_SUMMARY_KEY } from 'constants/tableQueryKeys'
import React, { useEffect, useMemo, useState } from 'react'
import {
  PiExport,
  PiEye,
  PiEyeSlash,
  PiHandshake,
  PiPhone,
  PiPushPin,
  PiRocketLaunch,
  PiTag,
  PiTruck,
} from 'react-icons/pi'
import useApiService from 'services/api'
import { notifyError } from 'services/toast'
import {
  activeProductIds,
  useSelectedProductIds,
} from 'stores/SelectedProductsStore/SelectedProductsStore'
import { useFeatureFlagService } from 'utils/useFeatureFlagService'
import { useImperativePin } from 'components/QuickAccess/usePinTarget'
import { useTableLinkVariant } from 'components/Tables/CommonTableCells/TableLinkStyleContext'
import {
  useElasticRelevancySort,
  useManageAssignmentActions,
  useSortParams,
  useUnifiedSummaryRefetch,
} from 'components/Tables/restaurantTableHooks'
import { AssignToCampaignModal } from '../../../features/campaigns/AssignToCampaignModal'
import { useCampaignUnassigner } from '../../../features/campaigns/useCampaignUnassigner'
import { FilterIdentifier } from '../../../models/saved_view'
import { useOpportunitiesContext } from '../../../pages/app/opportunities/opportunities-context'
import { queryClient } from '../../../services/queryClient'
import { getTableQueryKey } from '../../../utils/getTableQueryKey'
import { CreateDealModalForTable } from '../../Deals/CreateDealModal/CreateDealModal'

import { IDropdownItem } from '../../UI/Dropdown/Dropdown'
import { ChangeProductViewModal } from '../../change-product-view-modal'
import * as S from '../CommonTable.styles'

import { useAuth } from 'context/authentication/useAuth'
import { usePreferences } from 'context/preferences/PreferencesContext'
import { DataTableContainer } from '../../DataTable/UI'
import { cn } from '../../UI/cn'
import { ChainsTableSearch } from './ChainsTableSearch'
import { ChainsTableVariant } from './chainsTableVariants'
import { ChainsSummary } from 'models/summaries'
import { useForwardChainsSummary } from './useForwardChainsSummary'
import { SiSalesforce } from 'react-icons/si'
import { toast } from 'react-toastify'
import GPOAssignModal from 'components/Modals/AssignmentModal/GPOAssignModal'
import { PiCalendarStar, PiShippingContainer } from 'react-icons/pi'
import { IChainTable } from './ChainsTable'
import ReminderModal from 'components/Reminders/ReminderModal'
import TableButtonSort from 'components/Buttons/TableButtons/TableButtonSort'
import { useUnifiedChainsTable } from 'services/opportunities/useUnifiedChainsTable'
import { getTargetType } from 'services/opportunities/targetTypeMap'

const PAGE_SIZE = 100

export function ChainsTableComponent(
  props: IChainTable & { variant: ChainsTableVariant }
) {
  const { variant } = props
  const api = useApiService()
  const featureFlag = useFeatureFlagService()
  const pin = useImperativePin()
  const linkVariant = useTableLinkVariant()
  const hasCampaign = !!props.baseFilters?.main_campaign
  const IDENTIFIER = 'CHAINS_TABLE' + (props.filterIdentifierModifier ?? '')
  const chainsFilterStore = filterStoreRepo.getStore(
    IDENTIFIER as FilterIdentifier
  )

  const { page, setPage, search, valueFilters } = chainsFilterStore()

  // FIR-7845: hidden-only mode is driven by the "Show Hidden" quick filter
  // in the filterset modal — a regular `show_hidden` value filter in the
  // store, so it flows into request params via useFilterParams (rows, count,
  // summary/stat cards) and participates in chips/clear-all/saved views.
  // Here it only flips the kebab action between Hide and Unhide.
  const showHidden = isShowHiddenActive(valueFilters)

  const { sorting, setSorting, filteredSortOptions } = useElasticRelevancySort(
    props.tableKey,
    search,
    'count',
    variant.sortableFieldOptions
  )

  const {
    state: { rowSelection, totalSelectedRows, isAllRowsSelected },
    methods: { clearSelectedRows, setTotalRowsInBackend },
  } = useDataTableContext()

  const { selectedTag } = useOpportunitiesContext()
  const { preferences } = usePreferences()
  // FIR-7845: hide/unhide writes are admin-only (server: IsCompanyAdmin).
  // When the user-types flag is off, useAuth reports everyone as Admin,
  // matching the server permission's flag-off passthrough.
  const { isAdmin } = useAuth()

  const [showProductSelection, setShowProductSelection] = useState(false)
  const [showAssignTags, setShowAssignTags] = useState(false)
  const [showAssignDistributors, setShowAssignDistributors] = useState(false)
  const [showAssignGPOs, setShowAssignGPOs] = useState(false)
  const [showContactRequest, setShowContactRequest] = useState(false)
  const [showReminderModal, setShowReminderModal] = useState(false)

  const [menuModal, setMenuModal] = useState<
    | {
        chainId: number
        chainName?: string
        itemsIds?: number[]
        menuLength?: number
      }
    | undefined
  >(undefined)

  const selectedProductsIds = useSelectedProductIds()

  const sortParams = useSortParams(sorting)

  const filtersAsParams = {
    ...useFilterParams(chainsFilterStore),
    ...props.baseFilters,
  }

  const filterAndSortParams = useMemo(() => {
    return {
      ...filtersAsParams,
      ...sortParams,
      // Carry the product selection so the unified opps summary's total_ltv is
      // product-aware (drops the -1 "all products" sentinel). The unified list
      // ignores it — product only scopes LTV, never rows — but the summary reads
      // it. Without this the LTV card would always show the all-products total.
      product_id: activeProductIds(selectedProductsIds),
      // FIR-7845: `show_hidden` (hidden-only mode) arrives inside
      // `filtersAsParams` like any other value filter. The unified
      // `/opportunities/` endpoint honors it (via `adaptUnifiedListParams`
      // passthrough); `useUnifiedChainsTable` below is called with this same
      // object, so its summary query picks up show_hidden too.
    }
  }, [filtersAsParams, sorting, props.baseFilters, selectedProductsIds])

  // FIR-6995 / FIR-6997: rows + count come from the unified /opportunities/
  // endpoint (target_type=restaurant). Rows are re-nested under `chain` so the
  // existing columns/links/actions resolve.
  const unifiedChains = useUnifiedChainsTable(filterAndSortParams)

  // FIR-7822: campaign assign/unassign must re-resolve a select-all through the
  // SAME unified engine as the rows, via `opportunities/campaigns/assign/` for
  // this target type.
  const unifiedTargetType = getTargetType('restaurant')

  const TABLE_QUERY_KEY = getTableQueryKey({
    tableKey: props.tableKey,
    filterParams: filterAndSortParams,
    products: selectedProductsIds,
    page: page,
  })

  // DATA FETCHING
  const { isFetching, data } = useQuery({
    staleTime: Infinity,
    queryKey: TABLE_QUERY_KEY,
    enabled: !featureFlag.isLoading,
    queryFn: async ({ signal }) => {
      clearSelectedRows()

      const [err, res] = await to(
        unifiedChains.listAction(
          {
            ...filterAndSortParams,
            page: page,
            product_id: selectedProductsIds,
          },
          signal
        )
      )

      if ((err as AxiosError)?.code === 'ERR_NETWORK') {
        notifyError('Sorry! Unable to complete this search, please try again.')
      }
      if (err) throw err

      return res
    },
  })
  // END DATA FETCHING

  const unassignFromCampaign = useCampaignUnassigner(variant.tableTypeSlug, {
    data: data?.results ?? [],
    accessorKey: 'contact_company_id',
    tableQueryKey: TABLE_QUERY_KEY,
    requestParams: filterAndSortParams,
    campaignId: props?.baseFilters?.main_campaign,
    setPage: setPage,
    totalCount: data?.count ?? 0,
    pageSize: PAGE_SIZE,
    unifiedTargetType: unifiedTargetType,
  })

  useEffect(() => {
    clearSelectedRows()
  }, [data])

  const [openDealModal, setOpenDealModal] = React.useState(false)

  const totalRowsSelected = useMemo(() => {
    return isAllRowsSelected
      ? totalSelectedRows
      : Object.keys(rowSelection).length
  }, [isAllRowsSelected, rowSelection])

  // ALL stat-card values (chains, Total Doors, LTV) come from the unified opps
  // summary — filter-scoped and product-aware. FIR-7845: the summary reads
  // `filterAndSortParams` (via `useUnifiedChainsTable` above), which carries
  // `show_hidden`, so the stat cards follow the Show Hidden quick filter
  // like the rows.
  const effectiveCount = unifiedChains.unifiedCount ?? 0
  const effectiveSummary: ChainsSummary = {
    count: effectiveCount,
    places_count: unifiedChains.unifiedPlacesCount ?? 0,
    total_ltv: unifiedChains.unifiedLtv ?? 0,
    // Mirror the pre-unified summary's avg_total_ltv = AVG(COALESCE(ltv, 0)) =
    // sum / chain count, so the LTV column's "Avg:" subtitle renders.
    // avg_pounds_per_year has no unified source yet (would need a separate
    // pounds sum), so only that one subtitle stays hidden.
    avg_total_ltv:
      effectiveCount > 0 ? (unifiedChains.unifiedLtv ?? 0) / effectiveCount : 0,
    avg_pounds_per_year: 0,
  }
  const effectiveSummaryLoading = unifiedChains.unifiedSummaryFetching

  useForwardChainsSummary({
    summary: effectiveSummary,
    isLoading: effectiveSummaryLoading,
    count: effectiveCount,
    preserveWhileLoading: variant.preserveSummaryWhileLoading,
    setChainsSummary: props.setChainsSummary,
    setTotalRowsInBackend,
  })

  const handleCreateDeals = () => {
    if (!data) return
    setOpenDealModal(true)
  }

  const [showExportModal, setShowExportModal] = useState<boolean>(false)
  const [showChainExportModal, setShowChainExportModal] =
    useState<boolean>(false)

  const selectedContactCompanyIds = useMemo(() => {
    const accessor = 'contact_company_id'
    return (
      data?.results
        ?.filter((_: any, i: number) => rowSelection[i])
        ?.map((r: any) => {
          return accessor.split('.').reduce((acc, key) => acc[key], r)
        }) ?? []
    )
  }, [data, rowSelection])

  const unselectedContactCompanyIds = useMemo(() => {
    if (!isAllRowsSelected) return []
    return (
      data?.results
        ?.filter((_, i) => !rowSelection[i])
        ?.map((r) => r.contact_company_id) ?? []
    )
  }, [data, rowSelection, isAllRowsSelected])

  const columns = variant.useColumns(
    effectiveSummary,
    setMenuModal,
    sorting,
    data,
    selectedProductsIds,
    preferences,
    props.baseFilters?.main_campaign
  )
  const hiddenColumnIds = useMemo(() => {
    const visibleIds = new Set(
      columns.map((col) => col.id).filter((id): id is string => !!id)
    )
    return Object.keys(CHAIN_COLUMNS).filter((id) => !visibleIds.has(id))
  }, [columns])

  const refetchChainsSummary = useUnifiedSummaryRefetch(
    queryClient,
    UNIFIED_CHAINS_SUMMARY_KEY,
    filterAndSortParams
  )

  const { apiTagsAction, apiDistributorsAction, apiGPOsAction } =
    useManageAssignmentActions({
      api,
      tableType: variant.tableTypeSlug,
      unifiedTargetType,
      refetchSummary: refetchChainsSummary,
    })

  const selectedContactCompany = useMemo(() => {
    if (selectedContactCompanyIds.length !== 1 || !data?.results)
      return undefined
    const selectedContactCompany = data.results.find(
      (item) => item.contact_company_id === selectedContactCompanyIds[0]
    )
    return selectedContactCompany
  }, [selectedContactCompanyIds, data?.results])

  const actions = useMemo(() => {
    const items: IDropdownItem[] = [
      {
        label: 'Create Deals',
        icon: <PiHandshake size={20} />,
        callback: handleCreateDeals,
      },
      {
        label: 'Manage Tags',
        icon: <PiTag size={20} />,
        callback: () => setShowAssignTags(true),
      },
      {
        label: 'Add Known Distributors',
        icon: <PiTruck size={20} />,
        callback: () => setShowAssignDistributors(true),
      },
      {
        label: 'Add GPO',
        icon: <PiShippingContainer size={20} />,
        callback: () => setShowAssignGPOs(true),
      },
      {
        label: 'Schedule Reminder',
        icon: <PiCalendarStar size={20} />,
        callback: () => setShowReminderModal(true),
        disabled: selectedContactCompanyIds.length !== 1,
        disabledTooltipText:
          'Please select exactly one chain to schedule a reminder',
      },
      {
        label:
          selectedContactCompanyIds.length === 1 &&
          pin.isPinned({
            targetType: 'company',
            targetId: selectedContactCompanyIds[0],
          })
            ? 'Unpin from homepage'
            : 'Pin to homepage',
        icon: <PiPushPin size={20} />,
        hidden: !featureFlag.enableQuickAccess,
        callback: () => {
          const id = selectedContactCompanyIds[0]
          const name =
            selectedContactCompany?.chain?.chain ??
            selectedContactCompany?.overriden_name ??
            'target'
          const nowPinned = pin.toggle({
            targetType: 'company',
            targetId: id,
          })
          toast.success(
            nowPinned
              ? `Pinned ${name} to homepage`
              : `Unpinned ${name} from homepage`
          )
        },
        disabled: selectedContactCompanyIds.length !== 1,
        disabledTooltipText:
          'Please select exactly one target to pin to homepage',
      },
      {
        label: 'Export Selected',
        icon: <PiExport size={20} />,
        callback: () => setShowExportModal(true),
      },
      {
        label: 'Push to Salesforce',
        icon: <SiSalesforce size={20} />,
        // Yes, it's vaporware atm.
        callback: () => {
          setTimeout(() => {
            toast.success('Successfully pushed to Salesforce')
          }, 1000)
        },
        hidden: !featureFlag.enablePushToSalesforce,
      },
    ]

    if (featureFlag.enableContactRequests) {
      items.push({
        label: 'Request Contacts',
        icon: <PiPhone size={20} />,
        callback: () => setShowContactRequest(true),
        disabled: totalRowsSelected > MAX_OPPORTUNITIES_REQUESTED,
        disabledTooltipText: `To request contacts, select less than ${MAX_OPPORTUNITIES_REQUESTED} opportunities.`,
      })
    }

    if (featureFlag.enableCampaigns) {
      if (!hasCampaign) {
        items.push({
          label: 'Push to Campaign',
          icon: <PiRocketLaunch size={20} />,
          subContent: (
            <AssignToCampaignModal
              data={data?.results ?? []}
              accessorKey={'contact_company_id'}
              tableQueryKey={TABLE_QUERY_KEY}
              requestParams={filterAndSortParams}
              companyTypeSlug={'restaurants-bars'}
              tableTypeSlug={variant.tableTypeSlug}
              unifiedTargetType={unifiedTargetType}
              filterIdentifier={IDENTIFIER as FilterIdentifier}
              buttonLess
            />
          ),
        })
      } else {
        items.push({
          label: 'Unassign from Campaign',
          icon: variant.unassignIcon,
          callback: () => unassignFromCampaign.unassign(),
        })
      }
    }

    items.push({
      label: showHidden ? 'Unhide' : 'Hide',
      icon: showHidden ? <PiEye size={20} /> : <PiEyeSlash size={20} />,
      // Admin-only write; viewing hidden rows stays open to everyone.
      hidden: !isAdmin,
      disabled: isAllRowsSelected || selectedContactCompanyIds.length === 0,
      disabledTooltipText:
        'Select specific rows to hide (select-all is not supported)',
      callback: async () => {
        await api.commonSetHidden({
          contactCompanyIds: selectedContactCompanyIds,
          hidden: !showHidden,
          tableType: variant.tableTypeSlug,
        })
        toast.success(
          showHidden
            ? `Unhid ${selectedContactCompanyIds.length} record(s)`
            : `Hid ${selectedContactCompanyIds.length} record(s)`
        )
      },
    })

    return items
  }, [
    api,
    data,
    featureFlag,
    hasCampaign,
    isAdmin,
    isAllRowsSelected,
    selectedContactCompanyIds,
    selectedContactCompany,
    showHidden,
    pin,
    variant,
  ])

  return (
    <DataTableContainer>
      <DataTable
        tableHeader={
          <S.SearchContainer>
            <div className="flex w-full gap-2 items-center">
              <ChainsTableSearch
                filterStore={chainsFilterStore}
                tableKey={props.tableKey}
              />

              <TableButtonProduct
                selectedProducts={selectedProductsIds}
                onClick={() => setShowProductSelection(true)}
              />
              <ChainsFilterset
                hideCampaignsFilter={false}
                filterIdentifier={IDENTIFIER as FilterIdentifier}
                hiddenColumnIds={hiddenColumnIds}
              />
              <ColumnSelectorRecipient tableKey={props.tableKey} />
              <TableButtonSort
                options={filteredSortOptions}
                onSelect={(value, direction) => {
                  return setSorting([{ id: value, desc: direction === 'desc' }])
                }}
                selectedValue={
                  sorting.length
                    ? {
                        value: sorting[0].id,
                        direction: sorting[0].desc ? 'desc' : 'asc',
                      }
                    : undefined
                }
              />
            </div>

            <div className={cn('flex flex-row justify-between gap-4')}>
              {linkVariant !== 'plain' && (
                <TableButtonAction
                  items={actions}
                  disabled={!totalSelectedRows}
                  selectedRowsCount={totalSelectedRows ?? 0}
                />
              )}
              <FilterChips
                identifier={IDENTIFIER as FilterIdentifier}
                store={chainsFilterStore}
                showActive
                clearAllButton
              />
            </div>
          </S.SearchContainer>
        }
        selectHeaderActions={
          linkVariant === 'plain' ? (
            <TableButtonAction
              items={actions}
              disabled={!totalSelectedRows}
              selectedRowsCount={totalSelectedRows ?? 0}
            />
          ) : undefined
        }
        tableKey={props.tableKey}
        loading={isFetching || unassignFromCampaign.isUnassigning}
        data={data?.results ?? []}
        columns={columns}
        sortableFields={[
          ...variant.sortableFields,
          ...(featureFlag.enableTagsSort ? ['tags'] : []),
        ]}
        virtualizeRows
        enableRowSelection
        enableColumnResizing
        rowHeight={44}
        headerHeight={40}
        stickyFirstColumn={linkVariant === 'plain'}
        isPaginationEnabled={true}
        defaultSort={[{ id: 'count' as any, desc: true }]}
        paginationOptions={{
          totalRows: effectiveCount,
          pageSize: PAGE_SIZE,
          setPage: setPage,
          page: page,
          isPaginationLoading: isFetching,
        }}
        selectAllText="Select Targets"
        footerControls={
          <OpportunitiesTableFooterControls
            hideCreateCompanyButton={props.hideCreateCompanyButton}
            companyTypeSlug={'restaurants-bars'}
            onDownloadButtonClick={() =>
              selectedTag === 'fb-companies'
                ? setShowChainExportModal(true)
                : setShowExportModal(true)
            }
          />
        }
      />

      <CreateContactRequestJobModal
        data={data?.results ?? []}
        open={showContactRequest}
        onClose={() => setShowContactRequest(false)}
        // v1 and v2 keys
        accessorKeys={['contact_company_id', 'contact_company[0].id']}
        tableQueryKey={TABLE_QUERY_KEY}
        requestParams={filterAndSortParams}
        companyTypeSlug={'restaurants-bars'}
      />

      <TagAssignModal
        show={showAssignTags}
        handleClose={() => setShowAssignTags(false)}
        tableQueryKey={TABLE_QUERY_KEY}
        data={data?.results ?? []}
        filterAndSortParams={filterAndSortParams}
        apiTagsAction={apiTagsAction}
        idAccessor="contact_company_id"
      />

      <DistributorAssignModal
        show={showAssignDistributors}
        handleClose={() => setShowAssignDistributors(false)}
        tableQueryKey={TABLE_QUERY_KEY}
        data={data?.results ?? []}
        idAccessor="contact_company_id"
        filterAndSortParams={filterAndSortParams}
        apiDistributorsAction={apiDistributorsAction}
      />

      <GPOAssignModal
        show={showAssignGPOs}
        handleClose={() => setShowAssignGPOs(false)}
        tableQueryKey={TABLE_QUERY_KEY}
        data={data?.results ?? []}
        idAccessor="contact_company_id"
        filterAndSortParams={filterAndSortParams}
        apiGPOsAction={apiGPOsAction}
      />

      <ChainExportModal
        tableTypeSlug={variant.tableTypeSlug}
        count={
          (showExportModal
            ? selectedContactCompanyIds?.length
            : effectiveCount) ?? 0
        }
        show={showExportModal || showChainExportModal}
        _handleClose={() => {
          setShowExportModal(false)
          setShowChainExportModal?.(false)
        }}
        filters={filterAndSortParams}
        productSelected={selectedProductsIds[0]}
        selectedIds={!isAllRowsSelected ? selectedContactCompanyIds : undefined}
        excludeIds={isAllRowsSelected ? unselectedContactCompanyIds : undefined}
        unifiedTargetType={unifiedTargetType}
      />

      {menuModal?.chainId && (
        <MenuModal
          show={!!menuModal}
          handleClose={() => setMenuModal(undefined)}
          chainId={menuModal.chainId!}
          chainName={menuModal.chainName!}
          menuLength={menuModal?.menuLength}
          filterStore={chainsFilterStore}
        />
      )}

      <CreateDealModalForTable
        show={openDealModal}
        handleClose={() => setOpenDealModal(false)}
        queryKey={TABLE_QUERY_KEY}
        data={data?.results ?? []}
        idAccessor="contact_company_id"
        optimisticUiTarget="chains"
        filterAndSortParams={filterAndSortParams}
        onDealCreated={refetchChainsSummary}
        originCampaignId={props.baseFilters?.main_campaign}
        tableTypeSlug={variant.tableTypeSlug}
        companyTypeSlug="restaurants-bars"
        unifiedTargetType={unifiedTargetType}
      />

      <ChangeProductViewModal
        show={showProductSelection}
        handleClose={() => setShowProductSelection(false)}
      />

      {featureFlag.enableReminders && (
        <ReminderModal
          open={showReminderModal}
          onClose={() => setShowReminderModal(false)}
          defaultCompanyId={selectedContactCompany?.contact_company_id}
          onCreated={() => {
            setShowReminderModal(false)
          }}
        />
      )}
    </DataTableContainer>
  )
}
